import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, FileText, Receipt, Newspaper, LogOut, Plus, Loader2, Trash2, ShieldCheck, MessageSquare, Mail, Send, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  getClients, getDossiers, getInvoices, getBlog,
  createDossier, updateDossier, createInvoice, updateInvoice,
  createArticle, deleteArticle, getAssistantQueries, getNewsletter, sendNewsletter,
  adminListJobs, adminCreateJob, adminUpdateJob, adminDeleteJob, adminListApplications,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DOSSIER_STATUS = ["Diagnostic", "En cours", "Suivi & Reporting", "Terminé"];
const INVOICE_STATUS = ["En attente", "Payée", "Annulée"];

const TABS = [
  { key: "dossiers", label: "Dossiers", icon: FileText },
  { key: "factures", label: "Factures", icon: Receipt },
  { key: "blog", label: "Blog", icon: Newspaper },
  { key: "careers", label: "Offres d'emploi", icon: Briefcase },
  { key: "assistant", label: "Assistant", icon: MessageSquare },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "clients", label: "Clients", icon: Users },
];

const CONTRACT_TYPES = ["CDI", "CDD", "Stage", "Alternance", "Freelance"];

const ClientSelect = ({ clients, value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
    <SelectContent>
      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function AdminBackoffice() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dossiers");
  const [clients, setClients] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [analytics, setAnalytics] = useState({ total: 0, top_themes: [], recent: [] });
  const [newsletter, setNewsletter] = useState({ total: 0, subscribers: [], last_sent: null });
  const [sendingNl, setSendingNl] = useState(false);
  const [loading, setLoading] = useState(true);

  const [dForm, setDForm] = useState({ user_id: "", title: "", country: "", pillar: "conseil-entreprises", status: "Diagnostic" });
  const [iForm, setIForm] = useState({ user_id: "", label: "", amount: "", status: "En attente" });
  const [bForm, setBForm] = useState({ title: "", category: "", excerpt: "", img: "", content: "", featured: false });
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jForm, setJForm] = useState({ title: "", location: "Paris, France", contract: "CDI", department: "", description: "", responsibilities: "", requirements: "", apply_email: "", active: true });
  const [busy, setBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([getClients(), getDossiers(), getInvoices(), getBlog(), getAssistantQueries(), getNewsletter(), adminListJobs(), adminListApplications()])
      .then(([c, d, i, b, a, n, j, ap]) => { setClients(c); setDossiers(d); setInvoices(i); setArticles(b); setAnalytics(a); setNewsletter(n); setJobs(j); setApplications(ap); })
      .catch(() => toast.error("Chargement impossible."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const addDossier = async (e) => {
    e.preventDefault();
    if (!dForm.user_id || !dForm.title) return toast.error("Client et titre requis.");
    setBusy(true);
    try {
      await createDossier({ ...dForm, progress: 0, steps: [
        { label: "Écoute & Diagnostic", done: false }, { label: "Proposition sur-mesure", done: false },
        { label: "Mise en œuvre", done: false }, { label: "Suivi & Reporting", done: false }] });
      toast.success("Dossier créé — client notifié par email.");
      setDForm({ user_id: "", title: "", country: "", pillar: "conseil-entreprises", status: "Diagnostic" });
      reload();
    } catch { toast.error("Création impossible."); } finally { setBusy(false); }
  };

  const saveDossier = async (d, changes) => {
    try {
      const updated = await updateDossier(d.id, changes);
      setDossiers((prev) => prev.map((x) => (x.id === d.id ? updated : x)));
      toast.success("Dossier mis à jour" + (changes.status ? " — client notifié." : "."));
    } catch { toast.error("Mise à jour impossible."); }
  };

  const addInvoice = async (e) => {
    e.preventDefault();
    if (!iForm.user_id || !iForm.label || !iForm.amount) return toast.error("Client, objet et montant requis.");
    setBusy(true);
    try {
      await createInvoice({ user_id: iForm.user_id, label: iForm.label, amount: parseFloat(iForm.amount), currency: "EUR", status: iForm.status });
      toast.success("Facture créée — client notifié par email.");
      setIForm({ user_id: "", label: "", amount: "", status: "En attente" });
      reload();
    } catch { toast.error("Création impossible."); } finally { setBusy(false); }
  };

  const saveInvoice = async (inv, status) => {
    try {
      const updated = await updateInvoice(inv.id, { status });
      setInvoices((prev) => prev.map((x) => (x.id === inv.id ? updated : x)));
      toast.success("Facture mise à jour.");
    } catch { toast.error("Mise à jour impossible."); }
  };

  const addArticle = async (e) => {
    e.preventDefault();
    if (!bForm.title || !bForm.category || !bForm.excerpt) return toast.error("Titre, catégorie et extrait requis.");
    setBusy(true);
    try {
      await createArticle(bForm);
      toast.success("Article publié.");
      setBForm({ title: "", category: "", excerpt: "", img: "", content: "", featured: false });
      reload();
    } catch { toast.error("Publication impossible."); } finally { setBusy(false); }
  };

  const removeArticle = async (id) => {
    try { await deleteArticle(id); setArticles((p) => p.filter((a) => a.id !== id)); toast.success("Article supprimé."); }
    catch { toast.error("Suppression impossible."); }
  };

  const addJob = async (e) => {
    e.preventDefault();
    if (!jForm.title || !jForm.description) return toast.error("Titre et description requis.");
    setBusy(true);
    try {
      const payload = {
        ...jForm,
        responsibilities: jForm.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean),
        requirements: jForm.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const created = await adminCreateJob(payload);
      setJobs((p) => [created, ...p]);
      setJForm({ title: "", location: "Paris, France", contract: "CDI", department: "", description: "", responsibilities: "", requirements: "", apply_email: "", active: true });
      toast.success("Offre publiée.");
    } catch { toast.error("Publication impossible."); } finally { setBusy(false); }
  };

  const toggleJob = async (job) => {
    try {
      const updated = await adminUpdateJob(job.id, { active: !job.active });
      setJobs((p) => p.map((x) => (x.id === job.id ? updated : x)));
    } catch { toast.error("Mise à jour impossible."); }
  };

  const removeJob = async (id) => {
    if (!window.confirm("Supprimer définitivement cette offre ?")) return;
    try { await adminDeleteJob(id); setJobs((p) => p.filter((j) => j.id !== id)); toast.success("Offre supprimée."); }
    catch { toast.error("Suppression impossible."); }
  };

  const doSendNewsletter = async () => {
    setSendingNl(true);
    try {
      const res = await sendNewsletter();
      if (res.sent > 0) toast.success(`Veille envoyée à ${res.sent} abonné(s).`);
      else toast.error(`Aucun email n'a pu être envoyé (${res.subscribers} abonné(s)). Service d'envoi momentanément indisponible.`);
      const n = await getNewsletter(); setNewsletter(n);
    } catch { toast.error("Envoi impossible."); } finally { setSendingNl(false); }
  };

  const clientName = (uid) => clients.find((c) => c.id === uid)?.name || "—";

  return (
    <section className="min-h-screen bg-cream pt-28 pb-24" data-testid="admin-backoffice">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><ShieldCheck size={14} /> Back-office admin</span>
            <h1 className="mt-2 font-display text-4xl text-navy">Console de gestion</h1>
            <p className="mt-1 text-anthracite font-body">Gérez les dossiers, factures et le blog. {clients.length} clients.</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-sm font-medium text-navy hover:bg-navy hover:text-white transition-colors">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-navy/10">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-gold text-navy" : "border-transparent text-anthracite/60 hover:text-navy"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-gold" size={32} /></div>
        ) : (
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-8 grid gap-8 lg:grid-cols-3">

            {tab === "dossiers" && (
              <>
                <form onSubmit={addDossier} className="rounded-2xl border border-navy/10 bg-white p-6 lg:col-span-1 h-fit" data-testid="admin-dossier-form">
                  <h3 className="font-display text-lg text-navy flex items-center gap-2"><Plus size={16} className="text-gold" /> Nouveau dossier</h3>
                  <div className="mt-4 space-y-3">
                    <div><Label className="text-navy">Client</Label><ClientSelect clients={clients} value={dForm.user_id} onChange={(v) => setDForm({ ...dForm, user_id: v })} testid="admin-dossier-client" /></div>
                    <div><Label className="text-navy">Titre</Label><Input data-testid="admin-dossier-title" value={dForm.title} onChange={(e) => setDForm({ ...dForm, title: e.target.value })} className="mt-1.5" placeholder="Ex : Création SARL" /></div>
                    <div><Label className="text-navy">Pays</Label><Input value={dForm.country} onChange={(e) => setDForm({ ...dForm, country: e.target.value })} className="mt-1.5" placeholder="Bénin / Côte d'Ivoire" /></div>
                    <div><Label className="text-navy">Statut</Label>
                      <Select value={dForm.status} onValueChange={(v) => setDForm({ ...dForm, status: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>{DOSSIER_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button type="submit" disabled={busy} data-testid="admin-dossier-submit" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy disabled:opacity-60">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : "Créer le dossier"}
                  </button>
                </form>

                <div className="lg:col-span-2 space-y-4" data-testid="admin-dossiers-list">
                  {dossiers.map((d) => (
                    <div key={d.id} className="rounded-2xl border border-navy/10 bg-white p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div><p className="text-xs text-anthracite/60">{d.ref} · {clientName(d.user_id)} · {d.country}</p><h4 className="font-display text-navy">{d.title}</h4></div>
                      </div>
                      <div className="mt-4 grid sm:grid-cols-3 gap-3 items-end">
                        <div><Label className="text-xs text-navy">Statut</Label>
                          <Select value={d.status} onValueChange={(v) => saveDossier(d, { status: v })}>
                            <SelectTrigger data-testid={`admin-dossier-status-${d.ref}`} className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{DOSSIER_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs text-navy">Avancement %</Label>
                          <Input type="number" min="0" max="100" defaultValue={d.progress} className="mt-1"
                            onBlur={(e) => { const p = parseInt(e.target.value); if (!isNaN(p) && p !== d.progress) saveDossier(d, { progress: p }); }} />
                        </div>
                        <div className="text-xs text-anthracite/60">Progress: {d.progress}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab === "factures" && (
              <>
                <form onSubmit={addInvoice} className="rounded-2xl border border-navy/10 bg-white p-6 h-fit" data-testid="admin-invoice-form">
                  <h3 className="font-display text-lg text-navy flex items-center gap-2"><Plus size={16} className="text-gold" /> Nouvelle facture</h3>
                  <div className="mt-4 space-y-3">
                    <div><Label className="text-navy">Client</Label><ClientSelect clients={clients} value={iForm.user_id} onChange={(v) => setIForm({ ...iForm, user_id: v })} testid="admin-invoice-client" /></div>
                    <div><Label className="text-navy">Objet</Label><Input data-testid="admin-invoice-label" value={iForm.label} onChange={(e) => setIForm({ ...iForm, label: e.target.value })} className="mt-1.5" placeholder="Ex : Honoraires phase 1" /></div>
                    <div><Label className="text-navy">Montant (€)</Label><Input data-testid="admin-invoice-amount" type="number" value={iForm.amount} onChange={(e) => setIForm({ ...iForm, amount: e.target.value })} className="mt-1.5" placeholder="1500" /></div>
                  </div>
                  <button type="submit" disabled={busy} data-testid="admin-invoice-submit" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy disabled:opacity-60">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : "Créer la facture"}
                  </button>
                </form>
                <div className="lg:col-span-2 rounded-2xl border border-navy/10 bg-white overflow-hidden h-fit" data-testid="admin-invoices-list">
                  <table className="w-full text-left text-sm font-body">
                    <thead className="bg-cream text-xs uppercase tracking-wide text-anthracite/60"><tr><th className="px-5 py-3">N°</th><th className="px-5 py-3">Client</th><th className="px-5 py-3">Montant</th><th className="px-5 py-3">Statut</th></tr></thead>
                    <tbody className="divide-y divide-navy/5">
                      {invoices.map((inv) => (
                        <tr key={inv.id}>
                          <td className="px-5 py-3 font-medium text-navy">{inv.number}</td>
                          <td className="px-5 py-3 text-anthracite">{clientName(inv.user_id)}</td>
                          <td className="px-5 py-3 font-semibold text-navy">{inv.amount.toLocaleString("fr-FR")} €</td>
                          <td className="px-5 py-3 w-44">
                            <Select value={inv.status} onValueChange={(v) => saveInvoice(inv, v)}>
                              <SelectTrigger data-testid={`admin-invoice-status-${inv.number}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{INVOICE_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === "blog" && (
              <>
                <form onSubmit={addArticle} className="rounded-2xl border border-navy/10 bg-white p-6 h-fit" data-testid="admin-blog-form">
                  <h3 className="font-display text-lg text-navy flex items-center gap-2"><Plus size={16} className="text-gold" /> Nouvel article</h3>
                  <div className="mt-4 space-y-3">
                    <div><Label className="text-navy">Titre</Label><Input data-testid="admin-blog-title" value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} className="mt-1.5" /></div>
                    <div><Label className="text-navy">Catégorie</Label><Input data-testid="admin-blog-category" value={bForm.category} onChange={(e) => setBForm({ ...bForm, category: e.target.value })} className="mt-1.5" placeholder="Bénin / Côte d'Ivoire / …" /></div>
                    <div><Label className="text-navy">Image (URL)</Label><Input value={bForm.img} onChange={(e) => setBForm({ ...bForm, img: e.target.value })} className="mt-1.5" placeholder="https://…" /></div>
                    <div><Label className="text-navy">Extrait</Label><Textarea data-testid="admin-blog-excerpt" rows={2} value={bForm.excerpt} onChange={(e) => setBForm({ ...bForm, excerpt: e.target.value })} className="mt-1.5" /></div>
                    <div><Label className="text-navy">Contenu</Label><Textarea rows={3} value={bForm.content} onChange={(e) => setBForm({ ...bForm, content: e.target.value })} className="mt-1.5" /></div>
                    <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={bForm.featured} onChange={(e) => setBForm({ ...bForm, featured: e.target.checked })} /> Article à la une</label>
                  </div>
                  <button type="submit" disabled={busy} data-testid="admin-blog-submit" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy disabled:opacity-60">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : "Publier"}
                  </button>
                </form>
                <div className="lg:col-span-2 space-y-3" data-testid="admin-blog-list">
                  {articles.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-4 rounded-xl border border-navy/10 bg-white p-4">
                      <div className="flex items-center gap-3 min-w-0">
                        {a.img && <img src={a.img} alt="" className="h-12 w-16 object-cover rounded-lg shrink-0" />}
                        <div className="min-w-0"><p className="text-xs text-gold">{a.category}{a.featured ? " · À la une" : ""}</p><p className="font-display text-navy truncate">{a.title}</p></div>
                      </div>
                      <button onClick={() => removeArticle(a.id)} data-testid={`admin-blog-delete-${a.id}`} className="text-anthracite/50 hover:text-red-600 transition-colors shrink-0"><Trash2 size={17} /></button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab === "assistant" && (
              <div className="lg:col-span-3 grid gap-6 lg:grid-cols-3" data-testid="admin-assistant-analytics">
                <div className="rounded-2xl border border-navy/10 bg-white p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold">Total questions</p>
                  <p className="mt-2 font-display text-5xl text-navy">{analytics.total}</p>
                  <p className="mt-1 text-sm text-anthracite font-body">posées à l'Assistant Bridge Partners</p>
                  <h4 className="mt-6 font-display text-navy">Thématiques les plus recherchées</h4>
                  <div className="mt-3 space-y-2">
                    {analytics.top_themes.length === 0 && <p className="text-sm text-anthracite/60">Aucune donnée.</p>}
                    {analytics.top_themes.map((t) => {
                      const pct = analytics.total ? Math.round((t.count / analytics.total) * 100) : 0;
                      return (
                        <div key={t.theme}>
                          <div className="flex justify-between text-xs text-anthracite mb-1"><span>{t.theme}</span><span className="font-semibold text-navy">{t.count}</span></div>
                          <div className="h-2 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-gold to-gold-light" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="lg:col-span-2 rounded-2xl border border-navy/10 bg-white p-6">
                  <h4 className="font-display text-navy">Questions récentes</h4>
                  <div className="mt-4 space-y-3 max-h-[460px] overflow-y-auto">
                    {analytics.recent.length === 0 && <p className="text-sm text-anthracite/60">Aucune question pour le moment.</p>}
                    {analytics.recent.map((q) => (
                      <div key={q.id} className="rounded-xl border border-navy/10 p-4" data-testid={`admin-query-${q.id}`}>
                        <span className="inline-block rounded-full bg-gold/15 px-2.5 py-0.5 text-[11px] font-semibold text-gold">{q.theme}</span>
                        <p className="mt-2 text-sm text-navy font-body">{q.question}</p>
                        <p className="mt-1 text-xs text-anthracite/50">{new Date(q.created_at).toLocaleString("fr-FR")}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === "newsletter" && (
              <div className="lg:col-span-3 grid gap-6 lg:grid-cols-3" data-testid="admin-newsletter">
                <div className="rounded-2xl border border-navy/10 bg-white p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold">Abonnés à la veille</p>
                  <p className="mt-2 font-display text-5xl text-navy">{newsletter.total}</p>
                  <p className="mt-1 text-sm text-anthracite font-body">prospects inscrits</p>
                  <p className="mt-4 text-sm text-anthracite/70 font-body">Dernier envoi : {newsletter.last_sent ? new Date(newsletter.last_sent).toLocaleString("fr-FR") : "jamais"}</p>
                  <button onClick={doSendNewsletter} disabled={sendingNl || newsletter.total === 0} data-testid="newsletter-send-now"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy disabled:opacity-50">
                    {sendingNl ? <Loader2 size={16} className="animate-spin" /> : <><Send size={15} /> Envoyer la veille maintenant</>}
                  </button>
                  <p className="mt-3 text-xs text-anthracite/50 font-body">Envoi automatique chaque lundi. Vous pouvez aussi l'envoyer manuellement.</p>
                </div>
                <div className="lg:col-span-2 rounded-2xl border border-navy/10 bg-white overflow-hidden">
                  <table className="w-full text-left text-sm font-body">
                    <thead className="bg-cream text-xs uppercase tracking-wide text-anthracite/60"><tr><th className="px-5 py-3">Email</th><th className="px-5 py-3">Pays</th><th className="px-5 py-3">Inscrit le</th></tr></thead>
                    <tbody className="divide-y divide-navy/5">
                      {newsletter.subscribers.length === 0 && <tr><td colSpan={3} className="px-5 py-6 text-anthracite/60">Aucun abonné.</td></tr>}
                      {newsletter.subscribers.map((s) => (
                        <tr key={s.id}><td className="px-5 py-3 text-navy font-medium">{s.email}</td><td className="px-5 py-3 text-anthracite">{s.country || "—"}</td><td className="px-5 py-3 text-anthracite">{new Date(s.created_at).toLocaleDateString("fr-FR")}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "careers" && (
              <>
                <form onSubmit={addJob} className="rounded-2xl border border-navy/10 bg-white p-6 lg:col-span-1 h-fit" data-testid="admin-job-form">
                  <h3 className="font-display text-lg text-navy flex items-center gap-2"><Plus size={16} className="text-gold" /> Nouvelle offre</h3>
                  <div className="mt-4 space-y-3">
                    <div><Label>Titre du poste</Label><Input data-testid="job-title" value={jForm.title} onChange={(e) => setJForm({ ...jForm, title: e.target.value })} className="mt-1.5" placeholder="Ex : Consultant Compliance Senior" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label>Lieu</Label><Input data-testid="job-location" value={jForm.location} onChange={(e) => setJForm({ ...jForm, location: e.target.value })} className="mt-1.5" /></div>
                      <div><Label>Type</Label>
                        <Select value={jForm.contract} onValueChange={(v) => setJForm({ ...jForm, contract: v })}>
                          <SelectTrigger data-testid="job-contract" className="mt-1.5"><SelectValue /></SelectTrigger>
                          <SelectContent>{CONTRACT_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label>Département</Label><Input data-testid="job-department" value={jForm.department} onChange={(e) => setJForm({ ...jForm, department: e.target.value })} className="mt-1.5" placeholder="Ex : Compliance, Digital…" /></div>
                    <div><Label>Description</Label><Textarea data-testid="job-description" rows={4} value={jForm.description} onChange={(e) => setJForm({ ...jForm, description: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>Responsabilités (une par ligne)</Label><Textarea data-testid="job-responsibilities" rows={3} value={jForm.responsibilities} onChange={(e) => setJForm({ ...jForm, responsibilities: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>Profil recherché (un par ligne)</Label><Textarea data-testid="job-requirements" rows={3} value={jForm.requirements} onChange={(e) => setJForm({ ...jForm, requirements: e.target.value })} className="mt-1.5" /></div>
                    <div><Label>Email de candidature (optionnel)</Label><Input data-testid="job-apply-email" value={jForm.apply_email} onChange={(e) => setJForm({ ...jForm, apply_email: e.target.value })} className="mt-1.5" placeholder="rh@bridgepartners.fr" /></div>
                  </div>
                  <button type="submit" disabled={busy} data-testid="job-submit" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-3 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <>Publier l'offre <Send size={15} /></>}
                  </button>
                </form>

                <div className="lg:col-span-2 space-y-4">
                  <div className="rounded-2xl border border-navy/10 bg-white overflow-hidden" data-testid="admin-jobs-list">
                    <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
                      <h3 className="font-display text-lg text-navy">Offres publiées ({jobs.length})</h3>
                    </div>
                    {jobs.length === 0 ? (
                      <p className="p-6 text-sm text-anthracite/60 font-body">Aucune offre pour le moment.</p>
                    ) : (
                      <div className="divide-y divide-navy/5">
                        {jobs.map((j) => (
                          <div key={j.id} className="p-5 flex flex-wrap items-center gap-3" data-testid={`admin-job-row-${j.id}`}>
                            <div className="flex-1 min-w-[220px]">
                              <p className="font-display text-navy">{j.title}</p>
                              <p className="text-xs text-anthracite/70 font-body mt-0.5">{j.contract} · {j.location}{j.department ? ` · ${j.department}` : ""}</p>
                            </div>
                            <button onClick={() => toggleJob(j)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${j.active ? "bg-emerald-50 text-emerald-700" : "bg-anthracite/10 text-anthracite/60"}`} data-testid={`admin-job-toggle-${j.id}`}>
                              {j.active ? "Actif" : "Inactif"}
                            </button>
                            <button onClick={() => removeJob(j.id)} className="rounded-full p-2 text-anthracite/60 hover:bg-red-50 hover:text-red-600" data-testid={`admin-job-delete-${j.id}`}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {applications.length > 0 && (
                    <div className="rounded-2xl border border-navy/10 bg-white overflow-hidden" data-testid="admin-applications-list">
                      <div className="px-6 py-4 border-b border-navy/10">
                        <h3 className="font-display text-lg text-navy">Candidatures reçues ({applications.length})</h3>
                      </div>
                      <div className="divide-y divide-navy/5 max-h-[420px] overflow-y-auto">
                        {applications.map((a) => (
                          <div key={a.id} className="p-5">
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <div>
                                <p className="font-display text-navy">{a.name} <span className="text-sm text-anthracite/60 font-body">— {a.email}</span></p>
                                <p className="text-xs text-anthracite/70 font-body mt-0.5">Poste : <strong>{a.job_title}</strong>{a.phone ? ` · ${a.phone}` : ""}</p>
                              </div>
                              <span className="text-[11px] text-anthracite/50">{new Date(a.created_at).toLocaleDateString("fr-FR")}</span>
                            </div>
                            <p className="mt-2 text-sm text-anthracite font-body whitespace-pre-wrap">{a.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {tab === "clients" && (
              <div className="lg:col-span-3 rounded-2xl border border-navy/10 bg-white overflow-hidden" data-testid="admin-clients-list">
                <table className="w-full text-left text-sm font-body">
                  <thead className="bg-cream text-xs uppercase tracking-wide text-anthracite/60"><tr><th className="px-5 py-3">Nom</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Société</th><th className="px-5 py-3">Dossiers</th><th className="px-5 py-3">Factures</th></tr></thead>
                  <tbody className="divide-y divide-navy/5">
                    {clients.map((c) => (
                      <tr key={c.id}><td className="px-5 py-3 font-medium text-navy">{c.name}</td><td className="px-5 py-3 text-anthracite">{c.email}</td><td className="px-5 py-3 text-anthracite">{c.company || "—"}</td><td className="px-5 py-3">{c.dossiers_count}</td><td className="px-5 py-3">{c.invoices_count}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
